import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { batch } from 'react-redux';
import ModalTransactionComplete from '../../components/ModalTransactionComplete';
import {
  ChainExtended,
  selectBridgeSlice,
  setTxHasError,
  setIsConfirm,
} from '../../redux/slices/bridgeSlice';
import {
  getHistories,
  selectHistorySlice,
} from '../../redux/slices/historySlice';
import {
  resetSessionExpiredAt,
  selectWalletSlice,
} from '../../redux/slices/walletSlice';
import {
  useAppDispatch,
  useAppSelector,
  useInterval,
  useToChainWalletAddress,
} from '../../hooks';
import ModalWithdrawApproval from '../../components/ModalWithdrawApproval';
import TransactionTableItem from './TransactionTableItem';
import ModalTransactionFailed from '../../components/ModalTransactionFailed';
import ModalLogin from '../../components/Login/Login';
import ModalConfirmNoPreimg from '../../components/ModalConfirmNoPreimg';
import BridgeBackend, {
  HistoryPayload,
} from '../../services/bridge-backend.service';
import ModalTransactionDelay from '../../components/ModalTransactionDelay';
import KSTADIUM, {
  KStadiumJWTPayload,
  PostLoginResponse,
} from '../../services/kstadium.service';
import { decodePayloadFromJWT } from '../../utils/jwt';
import ModalDifferentId from '../../components/ModalDifferentId';
import HEXLANT from '../../utils/hexlant';
import buildHtlcService from '../../utils/buildHtlcService';
import ModalConnectWallet from '../../components/ModalConnectWallet';
import { setJwtTokens } from '../../redux/slices/kstadiumSlice';

const Loading = () => (
  <tr>
    <td colSpan={7} className="is-empty">
      Loading...
    </td>
  </tr>
);

const ITEMS_PER_PAGE = 10;
const KSTADIUM_BRIDGE_BACKEND_URL =
  process.env.REACT_APP_KSTADIUM_BRIDGE_BACKEND_URL!;

const TransactionTable: FC<{ walletAddress: string }> = ({ walletAddress }) => {
  const dispatch = useAppDispatch();
  const { histories, pageNumber } = useAppSelector(selectHistorySlice);
  // Refund 관련 State
  const [showRefundApproval, setShowRefundApproval] = useState(false);
  const [refundingHistory, setRefundingHistory] =
    useState<HistoryPayload | null>(null);
  const [isRefundFailed, setIsRefundFailed] = useState(false);
  const [refundTxHash, setRefundTxHash] = useState<string | null>(null);
  // Confirm 관련 State
  const [showConfirmApproval, setShowConfirmApproval] = useState(false);
  const [confirmingHistory, setConfirmingHistory] =
    useState<HistoryPayload | null>(null);
  const [confirmErrorType, setConfirmErrorType] = useState<
    null | 'no-preimg' | 'tx-failed'
  >(null);
  const [confirmTxHash, setConfirmTxHash] = useState<string | null>(null);
  const [showConfirmLoginModal, setConfirmShowLoginModal] = useState(false);
  const [showRefundComplete, setShowRefundComplete] = useState(false);
  const [showConfirmComplete, setShowConfirmComplete] = useState(false);
  const [showTransactionDelay, setShowTransactionDelay] = useState(false);
  const [connectWalletModalState, setConnectWalletModalState] = useState<
    false | 'from' | 'to'
  >(false);

  const {
    selectedFromChain,
    fromChainToken,
    fromChainHtlc,
    chains,
    selectedToChain,
    toChainToken,
    toChainHtlc,
    isConfirm,
  } = useAppSelector(selectBridgeSlice);
  const { fromChainWalletProvider, toChainWalletProvider } =
    useAppSelector(selectWalletSlice);
  const [selectedToChainAddress, setSelectedToChainAddress] = useState<
    string | null
  >(null);
  const [selectedFromChainAddress, setSelectedFromChainAddress] = useState<
    string | null
  >(null);
  const [isConfirmLogin, setIsConfirmLogin] = useState(false);

  const toChainWalletAddress = useToChainWalletAddress();

  const [uiSelectedConfirmChain, setUiSelectedConfirmChain] =
    useState<ChainExtended>();
  const [isInterval, setIsInterval] = useState(true);

  // Refund polling
  useInterval(
    async () => {
      if (!refundTxHash || !refundingHistory) return;
      try {
        const history = await BridgeBackend.getHistory(refundingHistory.id);

        if (history.fromChainHtlcState === `refunded`) {
          setShowTransactionDelay(false);
          setShowRefundComplete(true);
          setIsInterval(false);
        }
      } catch (error) {
        console.error(error);
      }
    },
    isInterval && refundTxHash && refundingHistory ? 5000 : null,
  );

  // Confirm polling
  useInterval(
    async () => {
      if (!confirmTxHash || !confirmingHistory) return;

      try {
        const history = await BridgeBackend.getHistory(confirmingHistory.id);
        const withdrawTransactionState =
          confirmingHistory.toChainSymbol === `KS_BESU`
            ? await KSTADIUM.getTransactionState(confirmTxHash)
            : null;

        if (history.toChainHtlcState === `withdrawn`) {
          setShowTransactionDelay(false);
          setShowConfirmComplete(true);
          setIsInterval(false);
        }

        if (withdrawTransactionState !== null) {
          if (withdrawTransactionState.resultCode !== `failed`) {
            // TxHash가 생성은 됬지만 toChain의 계정이 달라 transaction이 실패한 경우
            if (withdrawTransactionState.data.status === `true`) {
              setShowTransactionDelay(false);
              setConfirmErrorType(`tx-failed`);
              setConfirmTxHash(null);
            }
          }
        }
      } catch (error) {
        console.error(error);
      }
    },
    isInterval && confirmTxHash && confirmingHistory ? 5000 : null,
  );

  // Refund 실행
  const doRefund = useCallback(async () => {
    if (!refundingHistory) {
      console.error(`Refunding History not specified`);
      return;
    }

    if (!refundingHistory.hashlock) {
      console.error(`Refunding History does not containing fromContractId`);
      return;
    }
    setShowTransactionDelay(true);

    const foundChainToken = selectedFromChain?.chainTokenList.find(
      (token) => refundingHistory.tokenSymbol === token.symbol,
    );
    const foundChainHtlc = selectedFromChain?.chainHtlcList.find(
      (htlc) => htlc.isNative === !foundChainToken?.address,
    );

    if (refundingHistory.fromChainSymbol === `ETH`) {
      try {
        const htlcService = await buildHtlcService(
          fromChainWalletProvider,
          selectedFromChain,
          foundChainToken,
          foundChainHtlc,
        );

        const { tx } = await htlcService.refund(refundingHistory.hashlock);
        setRefundTxHash(tx.hash);
        dispatch(resetSessionExpiredAt());
      } catch (e) {
        setShowTransactionDelay(false);
        setIsRefundFailed(true);
      }
    }
    if (refundingHistory.fromChainSymbol === `KS_BESU`) {
      if (!foundChainHtlc) {
        console.error(`To chain HTLC not supported`);
        return;
      }
      try {
        const {
          data: { transactionHash },
        } = await HEXLANT.hexlantRefund(
          refundingHistory.hashlock,
          foundChainHtlc.address,
        );
        setRefundTxHash(transactionHash);
        dispatch(resetSessionExpiredAt());
      } catch (e) {
        setShowTransactionDelay(false);
        setIsRefundFailed(true);
      }
    }
  }, [dispatch, fromChainWalletProvider, refundingHistory, selectedFromChain]);

  // confirm 실행
  const doConfirmKS = useCallback(
    async (jwtTokens: PostLoginResponse['data']) => {
      dispatch(setJwtTokens(jwtTokens));

      const payload = decodePayloadFromJWT<KStadiumJWTPayload>(
        jwtTokens.accessToken,
      );

      if (!confirmingHistory) {
        console.error(`Confirming History not specified`);
        return;
      }
      // console.log(payload.info.account.toUpperCase());
      // console.log(selectedToChainAddress?.toUpperCase());
      if (confirmingHistory.toChainSymbol === `KS_BESU`) {
        if (
          payload.info.account.toUpperCase() !==
          selectedToChainAddress?.toUpperCase()
        ) {
          setIsConfirmLogin(true);
          return;
        }
      }
      if (!confirmingHistory.hashlock) {
        console.error(`Confirming History does not containing toContractId`);
        return;
      }
      const preimgHashMap = JSON.parse(
        localStorage.getItem(`preimgHashMap`) || `{}`,
      );
      const preimg = preimgHashMap[confirmingHistory.hashlock];

      if (!preimg) {
        setConfirmErrorType(`no-preimg`);
        return;
      }

      if (confirmingHistory.toChainHtlcState === `mirrored` && preimg) {
        setShowTransactionDelay(true);
      }

      // wallet provider를 판단하기 위한 정보가 history에 없기 때문에, toChain이 KS_BESU면 헥슬란트라고 가정함
      if (confirmingHistory.toChainSymbol === `KS_BESU`) {
        const toChainHistory = chains.find((c) => c.symbol === `KS_BESU`);

        const toChainTokenList = toChainHistory?.chainTokenList.find((token) =>
          token.symbol.includes(confirmingHistory.tokenSymbol),
        );

        const toChainHtlcHistory = toChainHistory?.chainHtlcList.find((h) =>
          toChainTokenList?.address ? !h.isNative : h.isNative,
        );
        if (!toChainHtlcHistory) {
          console.error(`To chain HTLC not supported`);
          return;
        }
        try {
          const {
            data: { transactionHash },
          } = await HEXLANT.hexlantWithdraw(
            toChainHtlcHistory.address,
            confirmingHistory.hashlock,
            preimg,
          );
          setConfirmTxHash(transactionHash);
          dispatch(resetSessionExpiredAt());
        } catch (e) {
          dispatch(setTxHasError(true));
          setConfirmErrorType(`tx-failed`);
          setShowTransactionDelay(false);
        }
      }
    },
    [chains, confirmingHistory, dispatch, selectedToChainAddress],
  );

  // confirm 실행
  const doConfirmConnectMetaMask = useCallback(async () => {
    setConnectWalletModalState(`to`);
    const selectedChain = chains.find(
      (chain) => chain.symbol === confirmingHistory?.toChainSymbol,
    );
    setUiSelectedConfirmChain(selectedChain);
  }, [chains, confirmingHistory?.toChainSymbol]);

  const doConfirm = useCallback(async () => {
    if (!confirmingHistory) {
      console.error(`Confirming History not specified`);
      return;
    }

    if (!confirmingHistory.hashlock) {
      console.error(`Confirming History does not containing toContractId`);
      return;
    }
    const preimgHashMap = JSON.parse(
      localStorage.getItem(`preimgHashMap`) || `{}`,
    );
    const preimg = preimgHashMap[confirmingHistory.hashlock];

    if (!preimg) {
      setConfirmErrorType(`no-preimg`);
      return;
    }

    if (confirmingHistory.toChainHtlcState === `mirrored` && preimg) {
      setShowTransactionDelay(true);
    }
    if (confirmingHistory.toChainSymbol === `ETH`) {
      const htlcService = await buildHtlcService(
        toChainWalletProvider,
        selectedToChain,
        toChainToken,
        toChainHtlc,
      );
      try {
        const { tx: withdrawTxHash } = await htlcService.withdraw(
          confirmingHistory.hashlock,
          preimg,
        );
        setConfirmTxHash(withdrawTxHash.hash);
        dispatch(resetSessionExpiredAt());
      } catch (e) {
        dispatch(setTxHasError(true));
        setConfirmErrorType(`tx-failed`);
        setShowTransactionDelay(false);
      }
    } else {
      console.error(`[withdraw] Unsupported ToChain`);
    }
  }, [
    confirmingHistory,
    dispatch,
    selectedToChain,
    toChainHtlc,
    toChainToken,
    toChainWalletProvider,
  ]);

  useEffect(() => {
    if (
      confirmingHistory?.fromChainSymbol === `KS_BESU` &&
      confirmingHistory?.fromChainHtlcState === `newContract`
    ) {
      dispatch(setIsConfirm(false));
    }
  }, [
    confirmingHistory?.fromChainHtlcState,
    confirmingHistory?.fromChainSymbol,
    dispatch,
  ]);

  useEffect(() => {
    if (!walletAddress || !fromChainToken) {
      return;
    }
    batch(() => {
      dispatch(
        getHistories({
          chainSymbol: fromChainToken.chainSymbol,
          sender: walletAddress,
          skip: (pageNumber - 1) * ITEMS_PER_PAGE,
          take: ITEMS_PER_PAGE,
        }),
      );
    });
  }, [dispatch, fromChainToken, pageNumber, walletAddress]);

  const onRequestClose = React.useCallback(() => {
    setConnectWalletModalState(false);
  }, []);

  const doConfirmRef = useRef<() => Promise<void>>(doConfirm);
  useEffect(() => {
    doConfirmRef.current = doConfirm;
  }, [dispatch, doConfirm]);

  return (
    <>
      <table className="table table-hover">
        <thead>
          <tr>
            <th>From Wallet address</th>
            <th>Token</th>
            <th>Amount</th>
            <th>Date-Time</th>
            <th>From Chain</th>
            <th>State</th>
            <th>To Wallet address</th>
            <th>To chain</th>
            <th>State</th>
          </tr>
        </thead>
        <tbody>
          {histories ? (
            histories.map((history) => (
              <TransactionTableItem
                key={history.id}
                data={history}
                onRequestRefund={() => {
                  setRefundingHistory(history);
                  setShowRefundApproval(true);
                }}
                onRequestConfirm={() => {
                  setConfirmingHistory(history);
                  setShowConfirmApproval(true);
                  setSelectedFromChainAddress(history.sender);
                  setSelectedToChainAddress(history.receiver);
                }}
              />
            ))
          ) : (
            <Loading />
          )}
        </tbody>
      </table>
      {/* Refund 관련 모달 */}
      {refundingHistory && (
        <>
          <ModalWithdrawApproval
            isShow={showRefundApproval}
            onRequestClose={() => setShowRefundApproval(false)}
            onAgree={doRefund}
          />
          <ModalTransactionFailed
            isShow={isRefundFailed}
            onRequestClose={() => setIsRefundFailed(false)}
          />
          <ModalTransactionComplete
            isShow={showRefundComplete}
            onRequestClose={() => window.location.replace(`/history`)}
            chainSymbol={refundingHistory.fromChainSymbol}
            txHash={refundTxHash}
          />
          <ModalTransactionDelay
            isShow={showTransactionDelay && confirmErrorType === null}
          />
        </>
      )}
      {/* Confirm 관련 모달 */}
      {confirmingHistory && (
        <>
          <ModalWithdrawApproval
            isShow={showConfirmApproval}
            onRequestClose={() => setShowConfirmApproval(false)}
            onAgree={() => {
              if (confirmingHistory.toChainSymbol === `KS_BESU`) {
                setConfirmShowLoginModal(true);
              } else {
                doConfirmConnectMetaMask();
              }
            }}
          />
          <ModalTransactionFailed
            isShow={confirmErrorType === `tx-failed`}
            onRequestClose={() => setConfirmErrorType(null)}
          />
          <ModalTransactionComplete
            isShow={showConfirmComplete}
            onRequestClose={() => window.location.replace(`/history`)}
            chainSymbol={confirmingHistory.toChainSymbol}
            txHash={confirmTxHash}
          />
          <ModalConfirmNoPreimg
            isShow={confirmErrorType === `no-preimg`}
            timelock={Number(confirmingHistory.toChainTimelock)}
            onRequestClose={() => setConfirmErrorType(null)}
          />
          <ModalTransactionDelay
            isShow={showTransactionDelay && confirmErrorType === null}
          />
          {showConfirmLoginModal && (
            <ModalLogin
              isShow
              onRequestClose={() => setConfirmShowLoginModal(false)}
              onSuccess={(result) => {
                if (result) doConfirmKS(result);
                setConfirmShowLoginModal(false);
              }}
              onFail={() => setConfirmShowLoginModal(false)}
            />
          )}
          <ModalDifferentId
            isShow={isConfirmLogin}
            onRequestClose={() => setIsConfirmLogin(false)}
          />
          <ModalConnectWallet
            selectedChain={uiSelectedConfirmChain}
            mode={connectWalletModalState}
            onRequestClose={onRequestClose}
            doConfirmRef={doConfirmRef}
          />
        </>
      )}
    </>
  );
};

export default TransactionTable;
